import React, { useEffect, useState } from "react";
import axios from "axios";
import { BASE_API_URL } from "store/Me";
import { Alert } from "components/ui/alert";
import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";

interface ProxyEditModalProps {
  open: boolean;
  proxyId: string;
  proxyName: string;
  currentUsername?: string;
  currentSharedKeyName?: string;
  onClose: (saved?: boolean) => void;
}

interface ProxyUpdatePayload {
  username: string;
  password?: string;
  sharedKeyName: string;
  sharedKey: string;
}

export default function SiteProxyEditModal({
  open,
  proxyId,
  proxyName,
  currentUsername,
  currentSharedKeyName,
  onClose,
}: ProxyEditModalProps) {
  const [username, setUsername] = useState(currentUsername ?? "");
  const [password, setPassword] = useState("");
  const [sharedKeyName, setSharedKeyName] = useState(currentSharedKeyName ?? "");
  const [sharedKey, setSharedKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setUsername(currentUsername ?? "");
      setPassword("");
      setSharedKeyName(currentSharedKeyName ?? "");
      setSharedKey("");
      setError(null);
    }
  }, [open, currentUsername, currentSharedKeyName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const payload: ProxyUpdatePayload = {
        username,
        sharedKeyName,
        sharedKey,
      };
      if (password) payload.password = password;
      await axios.put(`${BASE_API_URL}/api/proxies/${proxyId}`, payload);
      onClose(true);
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        err?.response?.data ??
        err?.message ??
        "Failed to update proxy.";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          onClose();
        }
      }}
    >
      <DialogContent
        aria-describedby={undefined}
        showCloseButton={false}
        className="sm:max-w-xs"
      >
        <DialogHeader>
          <DialogTitle>Edit Proxy — {proxyName}</DialogTitle>
        </DialogHeader>
        <form className="grid gap-4" onSubmit={handleSubmit}>
          {error && <Alert variant="destructive">{error}</Alert>}
          <div className="form-group">
            <label htmlFor="pe-username">Username</label>
            <input
              id="pe-username"
              className="form-control"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="pe-password">Password <small style={{ color: "gray" }}>(leave blank to keep current)</small></label>
            <input
              id="pe-password"
              type="password"
              className="form-control"
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="pe-sharedKeyName">Shared Key Name</label>
            <input
              id="pe-sharedKeyName"
              className="form-control"
              value={sharedKeyName}
              onChange={(e) => setSharedKeyName(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label htmlFor="pe-sharedKey">Shared Key</label>
            <input
              id="pe-sharedKey"
              type="password"
              autoComplete="new-password"
              className="form-control"
              value={sharedKey}
              onChange={(e) => setSharedKey(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button disabled={loading} onClick={() => onClose()} type="button" variant="outline">
              Cancel
            </Button>
            <Button type="submit" disabled={loading}>
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
